import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-description-cell',
    templateUrl: './description-cell.component.html',
    styleUrl: './description-cell.component.css',
    standalone: false,
})
export class DescriptionCellComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() description: string | null | undefined;
    @Input() emptyText: string = '-';
    @Output() expandedChange = new EventEmitter<boolean>();

    @ViewChild('descriptionCell') descriptionCellRef?: ElementRef<HTMLElement>;
    @ViewChild('measureEl') measureElRef?: ElementRef<HTMLElement>;

    isDescriptionExpanded: boolean = false;
    isDescriptionTruncated: boolean = false;
    displayedDescription: string = '';
    fullDescription: string = '';
    canFitDescription: boolean = false;

    private readonly SUFFIX = '… ';
    private resizeObserver?: ResizeObserver;

    constructor(
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    @HostListener('window:resize')
    onResize(): void {
        if (!this.isDescriptionExpanded) {
            this.computeTruncation();
        }
    }

    ngAfterViewInit(): void {
        const cell = this.descriptionCellRef?.nativeElement;
        if (!cell) {
            return;
        }

        this.resizeObserver = new ResizeObserver(entries => {
            const { width, height } = entries[0].contentRect;
            if (width > 0 && height > 0) {
                this.computeTruncation();
            }
        });
        this.resizeObserver.observe(cell);
        this.computeTruncation();
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['description'] || changes['emptyText']) {
            this.isDescriptionExpanded = false;
            this.computeTruncation();
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    toggleDescription(): void {
        this.isDescriptionExpanded = !this.isDescriptionExpanded;
        this.expandedChange.emit(this.isDescriptionExpanded);
    }

    computeTruncation(): void {
        this.canFitDescription = false;
        const cell = this.descriptionCellRef?.nativeElement;
        const measure = this.measureElRef?.nativeElement;
        this.fullDescription = this.description ?? '';

        if (!cell || !measure || !this.fullDescription) {
            this.displayedDescription = this.fullDescription || this.emptyText;
            this.isDescriptionTruncated = false;
            this.isDescriptionExpanded = false;
            return;
        }

        const cellStyle = getComputedStyle(cell);
        const paddingLeft = parseFloat(cellStyle.paddingLeft);
        const paddingRight = parseFloat(cellStyle.paddingRight);

        const contentWidth = cell.clientWidth - paddingLeft - paddingRight;

        if (contentWidth <= 0) {
            return;
        }

        const showMoreLabel = this.translate.instant('dlg.show-more');

        measure.style.width = `${contentWidth}px`;
        measure.textContent = 'Line 1\nLine 2';
        const detectedTwoLineHeight = measure.scrollHeight;

        measure.textContent = '';

        const maxHeight = detectedTwoLineHeight + 1;

        measure.textContent = this.fullDescription;

        if (measure.scrollHeight <= maxHeight) {
            this.displayedDescription = this.fullDescription;
            this.isDescriptionTruncated = false;
            this.isDescriptionExpanded = false;
            this.canFitDescription = true;
            return;
        }

        let low = 0;
        let high = this.fullDescription.length;
        let best = 0;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const candidateText = this.fullDescription.slice(0, mid).trimEnd() + this.SUFFIX;

            measure.textContent = candidateText + showMoreLabel;

            if (measure.scrollHeight <= maxHeight) {
                best = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        this.displayedDescription = this.fullDescription.slice(0, best).trimEnd() + this.SUFFIX;
        this.isDescriptionTruncated = true;

        if (this.isDescriptionExpanded) {
            this.displayedDescription = this.displayedDescription + ' ';
        }
    }
}
